import API from './api';
import { Urls } from './urls';
import { LocalStorage } from '../utils/storage';

const STORAGE_KEY = 'UserPreferences';

const isPlainObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

let saveQueue = Promise.resolve();

const enqueue = (task) => {
  saveQueue = saveQueue.then(task, task);
  return saveQueue;
};

const readAll = () => LocalStorage.get(STORAGE_KEY) || {};

const columnPreferencesFrom = (stored) => {
  const columns = {};
  Object.keys(stored).forEach((key) => {
    if (isPlainObject(stored[key])) {
      columns[key] = stored[key];
    }
  });
  return columns;
};

const pendingKeys = new Set();

const applyServerPreferences = (preferences, skipKeys = []) => {
  const current = readAll();
  const next = { ...current };
  Object.keys(preferences || {}).forEach((key) => {
    if (skipKeys.includes(key) || !isPlainObject(preferences[key])) {
      return;
    }
    next[key] = preferences[key];
  });
  LocalStorage.set(STORAGE_KEY, next);
};

const postColumnPreferences = (preferences) =>
  API.post(Urls.ApiTableColumnPreferences, {
    table_column_preferences: preferences,
  }).then((response) => {
    const current = response?.data?.table_column_preferences;
    return isPlainObject(current) ? current : null;
  });

const latestSaveByKey = {};

export const UserPreferences = {
  getRowsPerPage: function (defaultValue, prop) {
    const storedRowsPerPage = LocalStorage.getObjectKey(STORAGE_KEY, prop);
    return storedRowsPerPage != null ? storedRowsPerPage : defaultValue;
  },
  setRowsPerPage: function (rowsProp, numberOfRows) {
    LocalStorage.setObjectKey(STORAGE_KEY, rowsProp, numberOfRows);
  },
  getSelectedColumns: function (prop) {
    if (!prop) {
      return {};
    }
    const stored = LocalStorage.getObjectKey(STORAGE_KEY, prop);
    return stored && typeof stored === 'object' ? stored : {};
  },
  setSelectedColumns: function (prop, selectedColumns) {
    if (!prop) {
      return Promise.resolve(null);
    }
    const saveId = (latestSaveByKey[prop] || 0) + 1;
    latestSaveByKey[prop] = saveId;
    pendingKeys.add(prop);
    LocalStorage.setObjectKey(STORAGE_KEY, prop, selectedColumns);
    return enqueue(() => postColumnPreferences({ [prop]: selectedColumns }))
      .then((current) => {
        const latest = latestSaveByKey[prop] === saveId;
        if (latest) {
          pendingKeys.delete(prop);
        }
        if (!current || !latest) {
          return null;
        }
        applyServerPreferences(current, [...pendingKeys]);
        return current[prop] || selectedColumns;
      })
      .catch(() => {
        if (latestSaveByKey[prop] === saveId) {
          pendingKeys.delete(prop);
        }
        return null;
      });
  },
  hydrateFromLogin: function (serverPreferences) {
    if (!isPlainObject(serverPreferences)) {
      return Promise.resolve(null);
    }
    const local = columnPreferencesFrom(readAll());
    const missing = {};
    Object.keys(local).forEach((key) => {
      if (!(key in serverPreferences)) {
        missing[key] = local[key];
      }
    });
    applyServerPreferences({ ...local, ...serverPreferences });
    if (Object.keys(missing).length === 0) {
      return Promise.resolve(serverPreferences);
    }
    return enqueue(() => postColumnPreferences(missing))
      .then((current) => {
        if (current) {
          applyServerPreferences(current, [...pendingKeys]);
        }
        return current;
      })
      .catch(() => null);
  },
};
